import {
  GuestJourneyBenchmarksKpiCardDTO,
  GuestJourneyKpiCard,
} from 'api/reports-service/guest-journey-reports.types';

/**
 * Maps the BE `GET benchmarks/overview` payload (spec v0.5 —
 * `GuestJourneyBenchmarksKpiCardDTO[]`) onto the shared `GuestJourneyKpiCard`
 * the `KpiCard` renderer already consumes, so the Benchmarks KPI row reuses the
 * same card component as every other Guest Journey tab. Field map:
 * `eyebrow`→`title`, `value`→`value`, `baselineLabel`(+`baselineValue`)→
 * `valueDescription`, `delta`→`trend`, `description`→`footnote`.
 *
 * The live tab applies this at the endpoint boundary (`transformResponse`); the
 * fixture-backed locked preview applies it directly since it bypasses the query.
 * Guards non-array payloads (still-loading, stale mock worker, error bodies).
 */
export const toBenchmarksKpiCards = (
  cards?: GuestJourneyBenchmarksKpiCardDTO[],
): GuestJourneyKpiCard[] =>
  Array.isArray(cards)
    ? cards.map(card => ({
        title: card.eyebrow,
        value: card.value,
        valueDescription:
          card.baselineLabel && card.baselineValue
            ? `${card.baselineLabel} ${card.baselineValue}`
            : card.baselineLabel ?? undefined,
        trend: card.delta ?? undefined,
        footnote: card.description,
      }))
    : [];

/**
 * The "Brand Cuisine" KPI, prepended FE-side as the first card in the Benchmarks
 * KPI row. Unlike the benchmark cards it is not part of `GET benchmarks/overview`
 * — its value comes from the client cuisine bootstrap (`GET guest-journey/cuisine`),
 * and its label/description are FE-translated. Shared by the live tab and the
 * fixture-backed locked preview so the two never drift.
 */
export const buildBrandCuisineKpiCard = (
  t: (key: string) => string,
  cuisine: string,
): GuestJourneyKpiCard => ({
  title: t('page.guestJourney.benchmarks.brandCuisineKpi.title'),
  value: cuisine,
  valueDescription: t(
    'page.guestJourney.benchmarks.brandCuisineKpi.description',
  ),
});
